import { Alert, Button, Checkbox, Col, Form, Input, message, Row, Select, Switch } from 'antd'
import { invalidFieldsError } from 'components/InvalidFieldsError/InvalidFieldsError'
import SettingsCard from 'egi/app/Settings/components/SettignsCard/SettingsCard'
import React, { useEffect, useState } from 'react'
import { IPartnerCallbackConfiguration, IPartnerDetail, PartnerUpdateBody, callbackTypes, optionsWithSaved, resultTriggers, statusTriggers, unsupportedTriggers } from '../partnerApiCredentials'
import SecretInput from './SecretInput'

interface IProps {
  promoter: IPartnerDetail
  onSave: (body: PartnerUpdateBody) => Promise<void>
}

const methodOptions = [
  { label: 'GET', value: 'get' },
  { label: 'POST', value: 'post' },
  { label: 'PATCH', value: 'patch' }
]

const authTypeOptions = [
  { label: 'Bearer', value: 'bearer' },
  { label: 'Basic', value: 'basic' }
]

const responseConfigOptions = [
  { label: 'Body', value: 'body' },
  { label: 'Query', value: 'query' }
]

const callbackTypeOptions = callbackTypes.map(callbackType => ({ label: callbackType, value: callbackType }))

export default function CallbackSection ({ promoter, onSave }: IProps) {
  const [form] = Form.useForm<IPartnerCallbackConfiguration>()
  const [saving, setSaving] = useState(false)
  const [editingToken, setEditingToken] = useState(false)
  const savedToken = promoter.callbackConfiguration?.token ?? ''
  const callbackConfigurationKey = JSON.stringify(promoter.callbackConfiguration)
  const savedTriggers = promoter.callbackConfiguration?.triggers
  const outOfPattern = unsupportedTriggers(savedTriggers)

  useEffect(() => {
    const config: IPartnerCallbackConfiguration = callbackConfigurationKey ? JSON.parse(callbackConfigurationKey) : {}

    setEditingToken(false)

    form.setFieldsValue({
      url: config.url,
      token: config.token,
      method: config.method ?? 'get',
      authType: config.authType ?? 'bearer',
      responseConfig: config.responseConfig ?? [],
      useProxy: Boolean(config.useProxy),
      typeSpecific: config.typeSpecific ?? [],
      triggers: {
        status: config.triggers?.status ?? [],
        department: config.triggers?.department ?? [],
        step: config.triggers?.step ?? []
      }
    })
  }, [form, callbackConfigurationKey])

  const save = async (values: IPartnerCallbackConfiguration) => {
    setSaving(true)
    try {
      const { token, responseConfig, ...rest } = values
      const hadResponseConfig = Boolean(promoter.callbackConfiguration?.responseConfig?.length)
      await onSave({
        callbackConfiguration: {
          ...rest,
          ...(token && token !== savedToken ? { token } : {}),
          ...(responseConfig?.length || hadResponseConfig ? { responseConfig } : {})
        }
      })
    } catch (err) {
      if (err.data?.invalid) invalidFieldsError({ invalidFields: err.data.invalid })
      else message.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard className="mb-2">
      <SettingsCard.title>Callback</SettingsCard.title>
      <SettingsCard.description>Para onde e como a AGX envia os avisos de mudança de status ao parceiro.</SettingsCard.description>

      <Form form={form} layout="vertical" className="mt-3" onFinish={save}>
        <Row gutter={24}>
          <Col lg={12} xs={24}>
            <Form.Item name="url" label="URL do callback">
              <Input placeholder="https://" />
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item name="token" label="Token de saída">
              <SecretInput
                concealed
                readOnly={Boolean(savedToken) && !editingToken}
                onEdit={() => {
                  setEditingToken(true)
                  form.setFieldsValue({ token: '' })
                }}
                editing={editingToken}
                onCancel={() => {
                  setEditingToken(false)
                  form.setFieldsValue({ token: savedToken })
                }}
              />
            </Form.Item>
          </Col>

          <Col lg={12} xs={24}>
            <Form.Item name="method" label="Método">
              <Select options={methodOptions} />
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item
              name="authType"
              label="Autenticação"
              extra="Define o prefixo do header Authorization enviado ao parceiro. No Basic, informe o token já codificado."
            >
              <Select options={authTypeOptions} />
            </Form.Item>
          </Col>

          <Col lg={12} xs={24}>
            <Form.Item
              name="responseConfig"
              label="Formato da resposta (responseConfig)"
              extra="Onde os campos do callback vão na chamada"
              rules={[{ required: Boolean(promoter.callbackConfiguration?.responseConfig?.length), type: 'array', message: 'Selecione ao menos uma opção' }]}
            >
              <Checkbox.Group options={responseConfigOptions} />
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item label="Proxy">
              <Form.Item name="useProxy" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
              <span className="ml-2">Usar proxy</span>
            </Form.Item>
          </Col>
        </Row>

        <h4 className="font-500">Callbacks por tipo</h4>
        <p className="font-size-12 color-gray mb-2">Use quando um tipo de evento precisa de URL ou método próprios (typeSpecific). Tipos como approved e ongoing valem para callbacks de departamento e passo com esse status.</p>
        <Form.List name="typeSpecific">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Row key={field.key} gutter={24} align="bottom">
                  <Col lg={6} xs={24}>
                    <Form.Item name={[field.name, 'callbackType']} label="Tipo" rules={[{ required: true, message: 'Selecione o tipo' }]}>
                      <Select options={callbackTypeOptions} />
                    </Form.Item>
                  </Col>
                  <Col lg={10} xs={24}>
                    <Form.Item name={[field.name, 'url']} label="URL">
                      <Input placeholder="Usa a URL principal" />
                    </Form.Item>
                  </Col>
                  <Col lg={4} xs={24}>
                    <Form.Item name={[field.name, 'method']} label="Método">
                      <Select options={methodOptions} />
                    </Form.Item>
                  </Col>
                  <Col lg={4} xs={24}>
                    <Form.Item>
                      <Button type="ghost" onClick={() => remove(field.name)}>Remover</Button>
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Button type="ghost" className="mb-3" onClick={() => add()}>+ Adicionar tipo</Button>
            </>
          )}
        </Form.List>

        <h4 className="font-500">Gatilhos</h4>
        <p className="font-size-12 color-gray mb-2">Eventos que disparam o envio do callback (triggers).</p>
        {outOfPattern.length > 0 && (
          <Alert
            className="mb-2"
            type="warning"
            showIcon
            message={`Gatilhos fora do padrão mantidos: ${outOfPattern.join(', ')}. Novos valores fora do padrão não são aceitos.`}
          />
        )}
        <Row gutter={24}>
          <Col lg={8} xs={24}>
            <Form.Item name={['triggers', 'status']} label="Status" extra="Opções: created, approved, reproved, formsFinished">
              <Select mode="multiple" placeholder="Selecione os status" options={optionsWithSaved(statusTriggers, savedTriggers?.status)} />
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item name={['triggers', 'department']} label="Departamento" extra="Opções: approved, reproved">
              <Select mode="multiple" placeholder="Selecione" options={optionsWithSaved(resultTriggers, savedTriggers?.department)} />
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item name={['triggers', 'step']} label="Passo" extra="Opções: approved, reproved">
              <Select mode="multiple" placeholder="Selecione" options={optionsWithSaved(resultTriggers, savedTriggers?.step)} />
            </Form.Item>
          </Col>
        </Row>

        <SettingsCard.ButtonsContainer>
          <SettingsCard.submitButton loading={saving}>Salvar</SettingsCard.submitButton>
        </SettingsCard.ButtonsContainer>
      </Form>
    </SettingsCard>
  )
}
